import React, { useEffect } from 'react';
import VarNewMainChart from './VarNewMainChart/VarNewMainChart.component';
import VarNewMainTable from './VarNewMainTable/VarNewMainTable.component';
import VarNewBreakdownTableComponent from './varNewBreakdownTable/VarNewBreakdownTable.component';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import {
  activeSectionSelector,
  createActiveFundSelectorBySection,
} from '../../../../redux/pages/selectors';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useValueAtRiskPageQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import type { ValueAtRiskPayload } from '../../../../api/rs-hg/risk/valueAtRisk.types';

const ValueAtRiskNew: React.FC<FundInfoComponentProps> = ({
  fundId,
  fundName,
  ...props
}) => {
  const dispatch = useDispatch();
  const positionDate = props.positionDate ?? '';

  // On the VAR page the fund fetch is done by the parent component, it is not fetched = UPDATE_ACTIVE_FUND =>  before titlebar components so when is set after the titlebar compoennts the titlebar selects are removed
  const currentSection = useSelector(activeSectionSelector);

  const activeFund = useSelector(
    createActiveFundSelectorBySection(currentSection),
  );

  const isPositionDateLoading = !!fundId && !positionDate;

  const valueAtRiskPageQuery = useValueAtRiskPageQuery(fundId, positionDate);

  // Add the select components to the title bar
  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Risk Metric: ',
        titleBarKey: 'riskMetric',
        values: [
          {
            label: 'VaR',
            value: 'var',
          },
          {
            label: 'C-VaR',
            value: 'cvar',
          },
          {
            label: 'D-VaR',
            value: 'dvar',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'var',
      },
      {
        titleBarTitle: 'Risk Output: ',
        titleBarKey: 'riskOutput',
        values: [
          {
            label: 'Cash',
            value: 'Cash',
          },
          {
            label: 'Relative',
            value: 'relative',
          },
          {
            label: 'Absolute',
            value: 'absolute',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'absolute',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('riskMetric', REMOVE_SELECT_FROM_TITLEBAR),
      );
      dispatch(
        removeTitleBarComponent('riskOutput', REMOVE_SELECT_FROM_TITLEBAR),
      );
    };
  }, [dispatch, activeFund]);

  return (
    <QueryBoundary<ValueAtRiskPayload>
      isLoading={isPositionDateLoading || valueAtRiskPageQuery.isLoading}
      isFetching={valueAtRiskPageQuery.isFetching}
      isError={valueAtRiskPageQuery.isError}
      error={valueAtRiskPageQuery.error}
      data={valueAtRiskPageQuery.data?.data}
      loadingMessages={
        isPositionDateLoading
          ? ['Loading position date...']
          : ['Loading your var data', 'This may take a few minutes...']
      }
      noDataMessage="No value at risk data available."
      errorMessage="Unable to load value at risk data."
    >
      {(pageData) => (
        <>
          <VarNewMainTable fundName={fundName} pageData={pageData} />
          <VarNewMainChart fundName={fundName} pageData={pageData} />
          <VarNewBreakdownTableComponent
            breakdownData={pageData.var_breakdown_table}
            positionDate={positionDate}
          />
        </>
      )}
    </QueryBoundary>
  );
};

export default ValueAtRiskNew;
